import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, Subject, switchMap, takeUntil, tap, timer } from 'rxjs';
import { DateTime } from 'luxon';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { LocalStorageFacade } from '@application/localstorage';
import {
  APP_CONFIG,
  App_url_type_by_AppSource_enum,
  AppSource,
  AppUrlOptions,
} from '@application/config';
import {
  AuthStore,
  DeviceLoginApprovedEvent,
  DeviceLoginHubToken,
  DeviceLoginService,
  DeviceLoginStartResponse,
  DeviceLoginStatus,
  ELocalStorageKeys,
  IAuthAccessToken,
  normalizeToCamelCase,
  toAuthAccessToken,
} from '@auth/data-access';
import { ErrorStore } from '@application/errors/data-access';
import { DeviceLoginRealtimeService } from './infra/device-login-realtime.service';
import { getDateTime } from '@shared/utils';

const DEVICE_ID_STORAGE_KEY = 'live-pickup-device-id';
const DEFAULT_APP_SOURCE = 'live-pickup';
const DEFAULT_ORIGIN = 'live-pickup-web';
const DEFAULT_DEVICE_NAME = 'Live Pickup';

export interface DeviceLoginState {
  attemptId: string | null;
  deviceCode: string | null;
  activationCode: string | null;
  verificationUri: string | null;
  verificationUriComplete: string | null;
  expiresIn: string | null;
  pollingIntervalInSeconds: number | null;
  status: DeviceLoginStatus | null;
}

const INITIAL_STATE: DeviceLoginState = {
  attemptId: null,
  deviceCode: null,
  activationCode: null,
  verificationUri: null,
  verificationUriComplete: null,
  expiresIn: null,
  pollingIntervalInSeconds: null,
  status: null,
};

/**
 * The activation code is shown to a human as two halves split by a hyphen
 * (e.g. `ABC-DEF` or `ABCD-2345`). Backend normalizes by stripping hyphens and
 * spaces, so the dash is purely cosmetic — splitting in half keeps the display
 * consistent regardless of whether the backend issues 6 or 8 char codes.
 */
function formatActivationCode(code: string | null): string {
  if (!code) return '';
  const compact = code.replace(/[-\s]/g, '');
  if (compact.length <= 4) return compact;
  const half = Math.ceil(compact.length / 2);
  return `${compact.slice(0, half)}-${compact.slice(half)}`;
}

function readOrCreateDeviceId(localStorageFacade: LocalStorageFacade): string {
  const existing = localStorageFacade.getItem(DEVICE_ID_STORAGE_KEY);
  if (typeof existing === 'string' && existing.length > 0) return existing;
  const generated =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `device-${DateTime.now().toMillis()}-${Math.random().toString(36).slice(2, 10)}`;
  localStorageFacade.setItem(DEVICE_ID_STORAGE_KEY, generated);
  return generated;
}

export const DeviceLoginStore = signalStore(
  { providedIn: 'root' },
  withState<DeviceLoginState>(INITIAL_STATE),
  withReqState('startSession'),
  withComputed((store) => ({
    formattedActivationCode: computed(() =>
      formatActivationCode(store.activationCode()),
    ),
    isReady: computed(
      () => store.activationCode() !== null && store.deviceCode() !== null,
    ),
  })),
  withMethods((
    store,
    deviceLoginService = inject(DeviceLoginService),
    realtimeService = inject(DeviceLoginRealtimeService),
    localStorageFacade = inject(LocalStorageFacade),
    authStore = inject(AuthStore),
    errorStore = inject(ErrorStore),
    router = inject(Router),
    appConfig = inject(APP_CONFIG),
  ) => {
    /**
     * Backend's `verificationUriComplete` points at the accounts app (e.g.
     * `https://accounts.actuar.com/device?code=...`), which doesn't host the
     * activation modal in this project. Override it with our own deep link to
     * `/access/live-pickup?code=...` on the platform-shell URL matching the
     * current appSource (Ediz/Actuar/Facilfit/Tickou).
     */
    const buildActivationDeepLink = (activationCode: string): string => {
      const sourceName = AppSource[appConfig.appSource] as
        | keyof typeof App_url_type_by_AppSource_enum
        | undefined;
      const urlKey =
        (sourceName && App_url_type_by_AppSource_enum[sourceName]) ||
        'ediz_app';
      const base =
        appConfig.appUrl[urlKey as AppUrlOptions] ?? appConfig.appUrl.ediz_app;
      return `${base}/access/live-pickup?code=${encodeURIComponent(activationCode)}`;
    };
    let destroy$ = new Subject<void>();
    /**
     * Guard against double-firing if a reconnect causes the hub to re-emit the
     * approved event for the same attempt: a second pass would re-`setAuthData`
     * and re-`navigate`, which is harmless but jarring. One-shot is cleaner.
     */
    let alreadyRedirected = false;

    const persistAndRedirect = (raw: DeviceLoginApprovedEvent): void => {
      if (alreadyRedirected) return;
      const event = normalizeToCamelCase<DeviceLoginApprovedEvent>(
        raw as unknown as Record<string, unknown>,
      );
      if (event.status !== DeviceLoginStatus.Approved || !event.token) return;
      const hubToken = normalizeToCamelCase<DeviceLoginHubToken>(
        event.token as unknown as Record<string, unknown>,
      );
      const authData: IAuthAccessToken = toAuthAccessToken(hubToken);
      alreadyRedirected = true;
      localStorageFacade.setItem(ELocalStorageKeys.auth, JSON.stringify(authData));
      authStore.setAuthData(authData);
      patchState(store, { status: DeviceLoginStatus.Approved });
      destroy$.next();
      destroy$.complete();
      destroy$ = new Subject<void>();
      router.navigate(['/']);
    };

    const subscribeRealtime = async (deviceCode: string): Promise<void> => {
      realtimeService.approved$
        .pipe(takeUntil(destroy$))
        .subscribe(persistAndRedirect);

      try {
        await realtimeService.start();
        await realtimeService.joinDeviceLogin(deviceCode);
      } catch (err) {
        console.warn('[device-login] hub start failed:', err);
      }
    };

    const applyStartResponse = (raw: DeviceLoginStartResponse): void => {
      const res = normalizeToCamelCase<DeviceLoginStartResponse>(
        raw as unknown as Record<string, unknown>,
      );
      const deepLink = res.activationCode
        ? buildActivationDeepLink(res.activationCode)
        : res.verificationUriComplete;
      patchState(store, {
        attemptId: res.attemptId,
        deviceCode: res.deviceCode,
        activationCode: res.activationCode,
        verificationUri: res.verificationUri,
        verificationUriComplete: deepLink,
        expiresIn: res.expiresIn,
        pollingIntervalInSeconds: res.pollingIntervalInSeconds,
        status: res.status,
        ...setPropLoaded('startSession'),
      });

      destroy$.next();
      destroy$.complete();
      destroy$ = new Subject<void>();
      alreadyRedirected = false;

      if (res.deviceCode) {
        subscribeRealtime(res.deviceCode);
      }

      if (res.expiresIn) {
        // Já convertido para a hora de parede da conta pelo `DeviceLoginService`
        // — ver `isSessionStale` abaixo.
        const expiry = getDateTime(res.expiresIn);
        if (expiry?.isValid) {
          const msUntilExpiry = expiry.toMillis() - DateTime.now().toMillis();
          timer(Math.max(msUntilExpiry, 0))
            .pipe(takeUntil(destroy$))
            .subscribe(() => startSession());
        }
      }
    };

    const startSession = rxMethod<void>(
      pipe(
        tap(() => patchState(store, setPropLoading('startSession'))),
        switchMap(() => {
          const deviceId = readOrCreateDeviceId(localStorageFacade);
          return deviceLoginService
            .start({
              deviceId,
              deviceName: DEFAULT_DEVICE_NAME,
              appSource: DEFAULT_APP_SOURCE,
              origin: DEFAULT_ORIGIN,
            })
            .pipe(
              tapResponse({
                next: applyStartResponse,
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('startSession', error));
                },
              }),
            );
        }),
      ),
    );

    const reset = (): void => {
      patchState(store, INITIAL_STATE, setPropInit('startSession'));
      alreadyRedirected = false;
    };

    return {
      startSession,
      reset,

      /**
       * Mesma leitura da expiração que o temporizador de renovação, para um
       * código morto ser detetado da mesma forma pelos dois caminhos — este
       * serve quem reemite ao retomar, porque um browser de TV congelado pode
       * deixar o temporizador por disparar.
       *
       * `expiresIn` já vem na hora de parede da conta: o `DeviceLoginService`
       * converte-o na fronteira (o nome não termina em `Utc`, logo o
       * `DateInterceptor` não lhe toca). Por isso lê-se com `getDateTime` e
       * compara-se diretamente com `DateTime.now()` — os dois no mesmo
       * referencial. Converter outra vez aqui deslocava a expiração pelo offset
       * da conta.
       */
      isSessionStale(): boolean {
        if (store.startSessionError()) return true;
        const expiresIn = store.expiresIn();
        if (!expiresIn) return true;
        const expiry = getDateTime(expiresIn);
        return !expiry?.isValid || expiry.toMillis() <= DateTime.now().toMillis();
      },

      async cancelSession(): Promise<void> {
        const deviceCode = store.deviceCode();
        destroy$.next();
        destroy$.complete();
        destroy$ = new Subject<void>();

        if (deviceCode) {
          deviceLoginService.cancel(deviceCode).subscribe({
            error: (err) => {
              console.error(err);
            },
          });
        }

        await realtimeService.stop();

        reset();
      },
    };
  }),
);
